import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { MapPin, Plus, Pencil, Tag } from 'lucide-react';
import { TeamLogo } from '@/lib/teamLogos';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import api from '@/lib/api';

export const MATCH_LABELS = {
  clasico: { name: 'Clasico', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', dot: 'bg-emerald-500' },
  derby: { name: 'Derby', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', dot: 'bg-green-500' },
  high_importance: { name: 'High Importance', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', dot: 'bg-red-500' },
  medium_importance: { name: 'Medium Importance', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', dot: 'bg-amber-500' },
  low_importance: { name: 'Low Importance', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', dot: 'bg-yellow-500' },
};

const statusColor = {
  upcoming: 'bg-blue-100 text-blue-700', today: 'bg-green-100 text-green-700',
  tomorrow: 'bg-amber-100 text-amber-700', ended: 'bg-gray-100 text-gray-600',
  closed: 'bg-red-100 text-red-700', cancelled: 'bg-gray-200 text-gray-500',
};

export default function MatchDetailDialog({ match, onClose, onMatchUpdate, onEdit }) {
  const { t } = useTranslation();
  const { hasPerm, user } = useAuth();
  const navigate = useNavigate();
  const isManager = user?.role === 'admin' || user?.role === 'account_manager';
  const canCreate = hasPerm('create_requests') || hasPerm('create_requests_on_behalf');

  const handleSetLabel = async (label) => {
    try {
      const res = await api.put(`/api/matches/${match.id}/label`, { label });
      toast.success(label ? `Label set to ${MATCH_LABELS[label]?.name}` : 'Label removed');
      if (onMatchUpdate) onMatchUpdate(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to set label'); }
  };

  return (
    <Dialog open={!!match} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 max-w-md p-0 overflow-hidden">
        {match && (
          <>
            <div className="px-5 pt-5 pb-3">
              <div className="flex items-center justify-between mb-1 pr-6">
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{t('round')} {match.round || '—'}</span>
                <div className="flex items-center gap-1.5">
                  {match.label && MATCH_LABELS[match.label] && (
                    <Badge className={`text-[10px] pointer-events-none select-none ${MATCH_LABELS[match.label].color}`}>
                      {MATCH_LABELS[match.label].name}
                    </Badge>
                  )}
                  <Badge className={`text-[10px] pointer-events-none select-none ${statusColor[match.status] || 'bg-gray-100 text-gray-600'}`}>
                    {match.status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </Badge>
                </div>
              </div>
              <div className="flex items-center justify-center gap-6 py-4">
                <div className="flex flex-col items-center gap-1.5 min-w-[80px]">
                  <TeamLogo name={match.home_team} badgeUrl={match.home_team_badge} size={36} />
                  <span className="section-title text-center">{match.home_team}</span>
                </div>
                <div className="text-center">
                  {match.status === 'ended' && match.home_score != null && match.away_score != null
                    ? <span className="text-xl font-bold text-gray-800 dark:text-gray-200">{match.home_score} - {match.away_score}</span>
                    : <span className="text-xs font-bold text-gray-300 dark:text-gray-600 uppercase">VS</span>
                  }
                </div>
                <div className="flex flex-col items-center gap-1.5 min-w-[80px]">
                  <TeamLogo name={match.away_team} badgeUrl={match.away_team_badge} size={36} />
                  <span className="section-title text-center">{match.away_team}</span>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-800 px-5 py-3 space-y-2">
              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <span className="font-medium text-gray-600 dark:text-gray-300 w-16">{t('date')}</span>
                <span>{match.match_date || '—'}</span>
                {match.kickoff_time && <span className="text-gray-400">· {match.kickoff_time}</span>}
              </div>
              {(match.stadium || match.city) && (
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <MapPin size={12} className="text-gray-400 shrink-0" />
                  <span>{match.stadium || ''}{match.stadium && match.city ? ', ' : ''}{match.city || ''}</span>
                </div>
              )}
              {match.season && (
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <span className="font-medium text-gray-600 dark:text-gray-300 w-16">Season</span>
                  <span>{match.season}</span>
                </div>
              )}
              {match.notes && (
                <div className="flex items-start gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <span className="font-medium text-gray-600 dark:text-gray-300 w-16 shrink-0">{t('notes')}</span>
                  <span>{match.notes}</span>
                </div>
              )}
            </div>

            {/* Label selector for account managers */}
            {isManager && (
              <div className="px-5 py-2.5 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
                <span className="text-xs text-gray-500 dark:text-gray-400 font-medium flex items-center gap-1.5"><Tag size={12} /> Label</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button data-testid={`label-trigger-${match.id}`} variant="outline" size="sm" className="h-7 text-[11px] border-gray-200 dark:border-gray-600 gap-1.5 px-3">
                      {match.label && MATCH_LABELS[match.label] ? (
                        <><span className={`w-2 h-2 rounded-full ${MATCH_LABELS[match.label].dot}`} />{MATCH_LABELS[match.label].name}</>
                      ) : 'Set Label'}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 min-w-[180px]">
                    {Object.entries(MATCH_LABELS).map(([key, cfg]) => (
                      <DropdownMenuItem key={key} data-testid={`label-option-${key}`} className="text-xs gap-2 cursor-pointer" onClick={() => handleSetLabel(key)}>
                        <span className={`w-2.5 h-2.5 rounded-full ${cfg.dot}`} /> {cfg.name}
                      </DropdownMenuItem>
                    ))}
                    {match.label && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem data-testid="label-option-clear" className="text-xs text-gray-400 cursor-pointer" onClick={() => handleSetLabel('')}>
                          Remove Label
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            {/* Create Request button */}
            {canCreate &&
             (match.status === 'upcoming' || match.status === 'today' || match.status === 'tomorrow' ||
              (match.status === 'ended' && hasPerm('create_requests_on_behalf'))) && (
              <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-800 flex justify-center">
                <Button
                  data-testid={`detail-create-request-${match.id}`}
                  size="sm"
                  className="bg-gray-900 hover:bg-gray-800 dark:bg-gray-100 dark:hover:bg-gray-200 dark:text-gray-900 text-white h-9 text-xs px-6"
                  onClick={() => { onClose(); navigate(`/requests/new?match_id=${match.id}`); }}
                >
                  <Plus size={14} className="mr-1.5" /> {t('create_request')}
                </Button>
              </div>
            )}

            {/* Edit button for managers */}
            {isManager && onEdit && (
              <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-800 flex justify-end">
                <Button
                  data-testid={`detail-edit-match-${match.id}`}
                  size="sm"
                  variant="outline"
                  className="border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 h-8 text-xs"
                  onClick={() => { onEdit(match); onClose(); }}
                >
                  <Pencil size={13} className="mr-1.5" /> {t('edit_match') || 'Edit Match'}
                </Button>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
